﻿// ============================================================================
// src/lib/broiler.js  —  broiler domain helpers + constants
// ----------------------------------------------------------------------------
// Lifted out of main.jsx as prep for Round 6 (inline-JSX view extraction).
// Pure module-scope functions + constants; no React, no App closure state.
// ============================================================================
import {toISO, addDays, todayISO, fmtS} from './dateUtils.js';
import {computeProjectedCount} from './layerHousing.js';
// layer_dailys rows are owned by src/lib/layerHousing.ts — it declares the
// columns the projection maths reads, and this module does nothing with them
// except forward them to computeProjectedCount. App.jsx selects `*`, so the
// named keys plus that module's index signature describe the row exactly.
import type {LayerDailyRow} from './layerHousing.js';
import type {SupabaseClient} from './supabaseTypes.js';
import {applyBroilerOperation} from './broilerBatchOperationApi.js';

// ── the shapes this module reads ────────────────────────────────────────────
//
// A broiler batch lives in app_store.'ppp-v4' as free-form JSON authored by the
// planner UI, so the numeric fields arrive as either a number or the string the
// form last wrote. That is why every read below goes through parseInt/parseFloat
// — which perform ToString on their argument, so the explicit String() calls
// added during conversion change nothing at runtime and only make the tolerated
// string input visible to the checker. The index signature keeps the keys this
// module does not read (the Production tab reads totalToProcessor off the same
// rows, for instance) addressable.
export type BroilerBatch = {
  id?: string | null;
  name?: string | null;
  breed?: string | null;
  status?: string | null;
  hatchDate?: string | null;
  processingDate?: string | null;
  birdCount?: number | string | null;
  birdCountActual?: number | string | null;
  mortalityCumulative?: number | string | null;
  brooderFeedLbs?: number | string | null;
  schoonerFeedLbs?: number | string | null;
  gritLbs?: number | string | null;
  // Hatch-to-processing weeks stamped when the hatchery sets a longer growout
  // (see hatcheryGrowoutWeeks). Absent on every batch saved before the stamp.
  growoutWeeks?: number | string | null;
  // Days in the brooder, a whole number BROODER_DAYS_MIN..MAX. Absent on every
  // batch saved before the field existed, which means BROODER_DAYS.
  brooderDays?: number | string | null;
  [key: string]: unknown;
};

// A poultry_dailys row as the stats helper reads it.
export type BroilerDailyRow = {
  batch_label?: string | null;
  feed_type?: string | null;
  feed_lbs?: number | string | null;
  grit_lbs?: number | string | null;
  mortality_count?: number | string | null;
  [column: string]: unknown;
};

// A layer_batches row plus the housing rows the layer-feed projection needs.
export type LayerBatchRow = {
  id?: string | null;
  brooder_entry_date?: string | null;
  arrival_date?: string | null;
  original_count?: number | string | null;
  [column: string]: unknown;
};

export type LayerHousingRow = {
  batch_id?: string | null;
  status?: string | null;
  current_count?: number | string | null;
  [column: string]: unknown;
};

export type FeedScheduleWeek = {week: number; phase: string; lbsPerBird: number; totalLbs: number};

export type BroilerTimeline = {brooderIn: string; brooderOut: string; schoonerIn: string; schoonerOut: string};

/** Recorded entry wins; otherwise chicks are planned to enter on hatch day. */
export function resolveBrooderEntryDate(hatchDate?: string | null, brooderIn?: string | null): string {
  return typeof brooderIn === 'string' && brooderIn.trim() ? brooderIn : hatchDate || '';
}

export type BroilerStats = {
  starterFeed: number;
  growerFeed: number;
  gritLbs: number;
  mortality: number;
  // Started birds minus mortality, clamped at 0. Started is the day-1 count, or
  // the ordered count until that is entered (see broilerStartedBirds).
  projectedBirds: number;
  startedBirds: number;
  // True while startedBirds is the ordered count, so the figure is an estimate.
  startedEstimated: boolean;
  mortPct: number;
  dailyCount: number;
  legacy: boolean;
};

export type BroilerBatchColor = {bg: string; tx: string; bd: string};

// ── the rows the app_store / weigh-in reads select ──────────────────────────

// `select('data')` on app_store: one jsonb column holding the whole ppp-v4
// array. It stays `unknown` here and is proved to be an array before use.
export type AppStoreDataRow = {data: unknown};

// `select('id, completed_at')` on weigh_in_sessions.
export type BroilerWeekSessionRow = {id: string; completed_at: string | null};

// `select('weight')` on weigh_ins.
export type WeighInWeightRow = {weight?: number | string | null};

// The {ok, message?} contract every app_store writer in this module returns.
export type BroilerWriteResult = {ok: boolean; message?: string};

export const BROODER_DAYS = 14;
export const CC_SCHOONER = 35;
export const WR_SCHOONER = 42;

// Per-batch brooder length. A batch may stay BROODER_DAYS_MIN..MAX whole days
// in the brooder; a missing value means BROODER_DAYS. Extra brooder days come
// out of schooner time: the total growout (and so the target hatch, the
// unbooked end date and the feed schedule) does not change (Ronnie,
// 2026-10-14).
export const BROODER_DAYS_MIN = 14;
export const BROODER_DAYS_MAX = 18;

// The stored value if it is a valid brooder length, else undefined. Accepts
// the number or the digit string a form control writes; anything else
// (fractions, out of range, junk) is not a brooder length.
export function validBrooderDays(value: unknown): number | undefined {
  const text = typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
  if (!/^\d+$/.test(text)) return undefined;
  const days = Number(text);
  return days >= BROODER_DAYS_MIN && days <= BROODER_DAYS_MAX ? days : undefined;
}

// True when a value is present but not a valid brooder length. Save refuses it.
export function isInvalidBrooderDays(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '' && validBrooderDays(value) === undefined;
}

// The brooder length the dates use: the valid stored value, else the default.
export function effectiveBrooderDays(value: unknown): number {
  return validBrooderDays(value) ?? BROODER_DAYS;
}

// Schooner moves are planned for Monday-Thursday. Friday, Saturday and Sunday
// get a soft warning; dates are never shifted. Date-only, read at local noon.
export function isWeekendSchoonerMove(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const day = new Date(String(iso).slice(0, 10) + 'T12:00:00').getDay();
  return day === 5 || day === 6 || day === 0;
}

// Housing slot identifiers + cleanout windows. Used by App's broiler batch
// conflict handling, the broiler BatchForm dropdowns, and LayerBatchesView's
// brooder/schooner pickers. Single source of truth for both programs.
export const BROODERS = ['1', '2', '3'];
export const SCHOONERS = ['1', '2&3', '4&5', '6&6A', '7&7A'];
export const BROODER_CLEANOUT = 3;
export const SCHOONER_CLEANOUT = 4;

// Date-range overlap helper (both ends inclusive, ISO date strings).
// Broiler-vs-broiler and broiler-vs-layer conflict detection both use it.
export function overlaps(a1: string, a2: string, b1: string, b2: string): boolean {
  return (
    new Date(a1 + 'T12:00:00') <= new Date(b2 + 'T12:00:00') && new Date(a2 + 'T12:00:00') >= new Date(b1 + 'T12:00:00')
  );
}

// Status enum + hatchery lists. Used by the broiler BatchForm dropdowns +
// App's openEdit() legacy-flag detection. calcPoultryStatus() in this file
// also owns the same status strings (the enum is implicit there).
export const STATUSES = ['planned', 'active', 'processed'];
export const ALL_HATCHERIES = [
  'So Big Farms',
  'Meyer Hatchery',
  'Welp Hatchery',
  'Myers Poultry',
  'Freedom Ranger Hatchery',
];
export const LEGACY_HATCHERIES = ['VALLEY FARMS', 'CREDO FARMS', 'CACKLE'];

// So Big Farms birds grow out 8 weeks hatch-to-processing instead of the
// Cornish Cross 7: the brooder stays BROODER_DAYS and the schooner runs one
// week longer. The length is STAMPED on the batch (`growoutWeeks`) when So Big
// Farms is selected, not derived from the hatchery name, so a So Big batch
// saved before this rule keeps its 7-week fallback and an unrelated edit can
// never shift it. A stamp only lengthens: White Ranger is already 8 weeks.
export const SO_BIG_FARMS = 'So Big Farms';
export const SO_BIG_FARMS_GROWOUT_WEEKS = 8;

// The growout stamp a newly selected hatchery carries, or undefined for the
// breed default.
export function hatcheryGrowoutWeeks(hatchery: string | null | undefined): number | undefined {
  return hatchery === SO_BIG_FARMS ? SO_BIG_FARMS_GROWOUT_WEEKS : undefined;
}

// Schooner days for a breed, lengthened by the batch's growout stamp if any.
export function schoonerDays(breed: string | null | undefined, growoutWeeks?: number | string | null): number {
  const breedDays = breed === 'WR' ? WR_SCHOONER : CC_SCHOONER;
  const weeks = parseInt(String(growoutWeeks));
  return weeks > 0 ? Math.max(breedDays, weeks * 7 - BROODER_DAYS) : breedDays;
}

// Hatch-to-processing weeks, as the breed picker labels them.
export function growoutWeeksFor(breed: string | null | undefined, growoutWeeks?: number | string | null): number {
  return (BROODER_DAYS + schoonerDays(breed, growoutWeeks)) / 7;
}

// Target hatch date given a processing date + breed. Inverts calcTimeline().
// (1 day of schooner gap + BROODER_DAYS + per-breed schooner length.)
export function calcTargetHatch(
  processingDate: string | null | undefined,
  breed: string | null | undefined,
  growoutWeeks?: number | string | null,
): string | null {
  if (!processingDate) return null;
  const totalDays = 1 + BROODER_DAYS + schoonerDays(breed, growoutWeeks);
  return toISO(addDays(processingDate, -totalDays));
}

// Suggest weekday hatch dates within +/-3d of a target — hatchery ships
// Mon-Fri only, so we offer the caller the five available weekdays.
export type SuggestedHatchDate = {iso: string; offset: number; day: string; label: string};

export function suggestHatchDates(targetISO: string | null | undefined): SuggestedHatchDate[] {
  if (!targetISO) return [];
  const out: SuggestedHatchDate[] = [];
  for (let i = -3; i <= 3; i++) {
    const d = addDays(targetISO, i);
    const dow = d.getDay();
    if (dow >= 1 && dow <= 5) {
      out.push({
        iso: toISO(d),
        offset: i,
        day: ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'][dow],
        label: fmtS(toISO(d)),
      });
    }
  }
  return out;
}

const FEED_BIRDS = 700; // target processed count (order 750, expect ~700 to processor)
const STARTER_TOTAL_LBS = 1500; // fixed cap per batch for both breeds
const STARTER_PER_BIRD = STARTER_TOTAL_LBS / FEED_BIRDS; // 2.14 lbs/bird (1500/700)
// Split starter 30/70 across weeks 1-2 (matches chick growth pattern)
const STARTER_W1 = STARTER_PER_BIRD * 0.3;
const STARTER_W2 = STARTER_PER_BIRD * 0.7;

// CC grower weeks 3-7 (5 weeks): derived from WCF historical data (2026-04 analysis)
// CC avg 14.29 lbs/bird at 7 weeks processing (7 data points, combining actual 7w batches +
// 8w batches back-calculated using 26.4% week 7→8 growth rate from Aviagen chart)
const CC_TOTAL_TARGET = 14.29;
const CC_GROWER_TARGET = CC_TOTAL_TARGET - STARTER_PER_BIRD; // 12.15 lbs/bird
const CC_GROWER_CHART = [2.482, 4.535, 7.24, 10.485, 14.129]; // cumulative at wks 3-7 from chart (shape only — scaled to WCF target)
const CC_GROWER_WEEKLY_CHART = CC_GROWER_CHART.map((c, i) => c - (i === 0 ? 1.109 : CC_GROWER_CHART[i - 1]));
const CC_GROWER_CHART_TOTAL = CC_GROWER_WEEKLY_CHART.reduce((a, b) => a + b, 0);
const CC_GROWER_SCALE = CC_GROWER_TARGET / CC_GROWER_CHART_TOTAL;
const CC_GROWER_WEEKLY = CC_GROWER_WEEKLY_CHART.map((w) => w * CC_GROWER_SCALE);

// WR grower weeks 3-8 (6 weeks): derived from WCF historical data (2026-04 analysis)
// WR avg 16.26 lbs/bird at 8 weeks processing (3 data points, combining actual 8w batches +
// 9w batches back-calculated using 23.5% week 8→9 growth rate from White Ranger Hatchery chart)
const WR_TOTAL_TARGET = 16.26;
const WR_GROWER_TARGET = WR_TOTAL_TARGET - STARTER_PER_BIRD; // 14.12 lbs/bird
const WR_GROWER_CHART = [2.3048, 3.975, 5.9292, 8.1483, 10.58, 13.365]; // cumulative wks 3-8
const WR_GROWER_WEEKLY_CHART = WR_GROWER_CHART.map((c, i) => c - (i === 0 ? 1.1193 : WR_GROWER_CHART[i - 1]));
const WR_GROWER_CHART_TOTAL = WR_GROWER_WEEKLY_CHART.reduce((a, b) => a + b, 0);
const WR_GROWER_SCALE = WR_GROWER_TARGET / WR_GROWER_CHART_TOTAL;
const WR_GROWER_WEEKLY = WR_GROWER_WEEKLY_CHART.map((w) => w * WR_GROWER_SCALE);

// CC grower week 8, only for a batch stamped with the So Big Farms 8-week
// growout. Source: Aviagen Ross 308 / Ross 308 FF Broiler Performance
// Objectives 2022, as-hatched, page 3: cumulative feed intake 7733 g at day 56
// minus 6115 g at day 49 = 1618 g/bird (3.57 lb/bird, 2,497.0 lb per 700 birds
// after getFeedSchedule's rounding). Used as published, not scaled: the WCF
// target above covers weeks 1-7 only. Approved by Ronnie via Codex 2026-10-14.
const CC_WEEK8_LBS_PER_BIRD = (7733 - 6115) / 453.59237;

export function getFeedSchedule(
  breed: string | null | undefined,
  growoutWeeks?: number | string | null,
): FeedScheduleWeek[] {
  const growerWeeks =
    breed === 'WR'
      ? WR_GROWER_WEEKLY
      : breed === 'CC' && growoutWeeksFor('CC', growoutWeeks) >= 8
        ? [...CC_GROWER_WEEKLY, CC_WEEK8_LBS_PER_BIRD]
        : CC_GROWER_WEEKLY;
  const weeks = [
    {week: 1, phase: 'starter', lbsPerBird: STARTER_W1},
    {week: 2, phase: 'starter', lbsPerBird: STARTER_W2},
    ...growerWeeks.map((lpb, i) => ({week: i + 3, phase: 'grower', lbsPerBird: lpb})),
  ];
  return weeks.map((w) => ({
    ...w,
    lbsPerBird: Math.round(w.lbsPerBird * 100) / 100,
    totalLbs: Math.round(w.lbsPerBird * FEED_BIRDS * 10) / 10,
  }));
}

export function calcBatchFeed(batch: BroilerBatch): {
  schedule: FeedScheduleWeek[];
  starter: number;
  grower: number;
  total: number;
} {
  const schedule = getFeedSchedule(batch.breed, batch.growoutWeeks);
  let starter = 0,
    grower = 0;
  schedule.forEach((w) => {
    if (w.phase === 'starter') starter += w.totalLbs;
    else grower += w.totalLbs;
  });
  return {schedule, starter: Math.round(starter), grower: Math.round(grower), total: Math.round(starter + grower)};
}

export const BREED_STYLE = {
  CC: {bg: '#E6F1FB', tx: '#185FA5'},
  WR: {bg: '#FAEEDA', tx: '#854F0B'},
  FR: {bg: '#F3E8FF', tx: '#6b21a8'}, // Freedom Rangers (legacy)
  CY: {bg: '#FFE4E6', tx: '#9f1239'}, // Color Yields (legacy)
};

export function calcTimeline(
  hatchDate: string | null | undefined,
  breed: string | null | undefined,
  processingDate: string | null | undefined,
  growoutWeeks?: number | string | null,
  brooderDays?: number | string | null,
): BroilerTimeline | null {
  if (!hatchDate) return null;
  const brooderIn = hatchDate;
  const brooderOut = toISO(addDays(hatchDate, effectiveBrooderDays(brooderDays)));
  const schoonerIn = brooderOut;
  // A booked processing date always wins; the growout length is only the
  // fallback for a batch that has none yet. That fallback is measured from a
  // standard BROODER_DAYS brooder, so extra brooder days shorten the schooner
  // instead of extending the growout.
  const schoonerOut = processingDate
    ? toISO(addDays(processingDate, -1))
    : toISO(addDays(hatchDate, BROODER_DAYS + schoonerDays(breed, growoutWeeks)));
  return {brooderIn, brooderOut, schoonerIn, schoonerOut};
}

function isISODateLike(value: unknown): boolean {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.slice(0, 10));
}

export function shouldAutoActivateBroilerBatch(
  batch: BroilerBatch | null | undefined,
  asOfDate: string | null | undefined = todayISO(),
): boolean {
  if (!batch || batch.status !== 'planned') return false;
  const hatchDate = String(batch.hatchDate || '').slice(0, 10);
  const today = String(asOfDate || todayISO()).slice(0, 10);
  if (!isISODateLike(hatchDate) || !isISODateLike(today)) return false;
  return today >= hatchDate;
}

// Auto-process an ACTIVE batch once its processing date arrives. "On the
// processing date" is inclusive (today >= processingDate). Mirrors the
// hatch-day auto-activation date style. Only active batches are eligible, so
// this never fights auto-activation (which acts on planned batches) and never
// overrides a manual/already-processed terminal status.
export function shouldAutoProcessBroilerBatch(
  batch: BroilerBatch | null | undefined,
  asOfDate: string | null | undefined = todayISO(),
): boolean {
  if (!batch || batch.status !== 'active') return false;
  const processingDate = String(batch.processingDate || '').slice(0, 10);
  const today = String(asOfDate || todayISO()).slice(0, 10);
  if (!isISODateLike(processingDate) || !isISODateLike(today)) return false;
  return today >= processingDate;
}

export function calcPoultryStatus(
  batch: BroilerBatch | null | undefined,
  asOfDate: string | null | undefined = todayISO(),
): string {
  // Manual terminal state wins. Planned is the one status that can age
  // forward automatically once hatch day arrives.
  if (batch && batch.status === 'processed') return 'processed';
  if (batch && batch.status === 'active') return 'active';
  if (!batch || !batch.hatchDate) return 'planned';
  const today = String(asOfDate || todayISO()).slice(0, 10);
  const tl = calcTimeline(batch.hatchDate, batch.breed, batch.processingDate, batch.growoutWeeks, batch.brooderDays);
  if (!tl) return 'planned';
  if (today < tl.brooderIn) return 'planned';
  if (!batch.status && batch.processingDate && today > batch.processingDate) return 'processed';
  return 'active';
}

// True when nobody has entered the batch's day-1 bird count yet.
export function broilerDayOneCountMissing(batch: BroilerBatch): boolean {
  return !((parseInt(String(batch.birdCountActual), 10) || 0) > 0);
}

// The birds a batch started with, for on-farm counts. The day-1 count
// (birdCountActual) once it is entered; until then the ordered birdCount,
// flagged `estimated` so the UI marks the figure 'est.' (constitution Q50).
// Home's broilersOnFarmAt has always credited ordered birds the same way
// (Ronnie's note 1, 2026-10-06: both counts must).
export function broilerStartedBirds(batch: BroilerBatch): {started: number; estimated: boolean} {
  if (!broilerDayOneCountMissing(batch)) {
    return {started: parseInt(String(batch.birdCountActual), 10), estimated: false};
  }
  const ordered = Math.max(0, parseInt(String(batch.birdCount), 10) || 0);
  return {started: ordered, estimated: ordered > 0};
}

// Live broiler stats computed from daily reports.
// For modern (non-B-24) batches this is the source of truth for feed/grit/mortality.
// For legacy B-24-* batches the stored fields on the batch are used (no daily reports exist).
// Returns: {starterFeed, growerFeed, gritLbs, mortality, projectedBirds, startedBirds,
// startedEstimated, mortPct, dailyCount}
export function calcBroilerStatsFromDailys(
  batch: BroilerBatch,
  broilerDailys: readonly BroilerDailyRow[] | null | undefined,
): BroilerStats {
  const isLegacy = /^b-24-/i.test(batch.name || '');
  const dayOneCount = parseInt(String(batch.birdCountActual)) || 0;
  const {started, estimated} = broilerStartedBirds(batch);
  if (isLegacy) {
    const mort = parseInt(String(batch.mortalityCumulative)) || 0;
    return {
      starterFeed: parseFloat(String(batch.brooderFeedLbs)) || 0,
      growerFeed: parseFloat(String(batch.schoonerFeedLbs)) || 0,
      gritLbs: parseFloat(String(batch.gritLbs)) || 0,
      mortality: mort,
      projectedBirds: Math.max(0, started - mort),
      startedBirds: started,
      startedEstimated: estimated,
      mortPct: dayOneCount > 0 ? (mort / dayOneCount) * 100 : 0,
      dailyCount: 0,
      legacy: true,
    };
  }
  const target = String(batch.name || '')
    .toLowerCase()
    .trim();
  const bd = (broilerDailys || []).filter((d) => {
    const lbl = String(d.batch_label || '')
      .toLowerCase()
      .trim()
      .replace(/^\(processed\)\s*/, '')
      .trim();
    return lbl === target;
  });
  let starterFeed = 0,
    growerFeed = 0,
    gritLbs = 0,
    mortality = 0;
  for (const d of bd) {
    const f = parseFloat(String(d.feed_lbs)) || 0;
    if (d.feed_type === 'STARTER') starterFeed += f;
    else if (d.feed_type === 'GROWER') growerFeed += f;
    gritLbs += parseFloat(String(d.grit_lbs)) || 0;
    mortality += parseInt(String(d.mortality_count)) || 0;
  }
  return {
    starterFeed: Math.round(starterFeed),
    growerFeed: Math.round(growerFeed),
    gritLbs: Math.round(gritLbs),
    mortality,
    projectedBirds: Math.max(0, started - mortality),
    startedBirds: started,
    startedEstimated: estimated,
    mortPct: dayOneCount > 0 ? (mortality / dayOneCount) * 100 : 0,
    dailyCount: bd.length,
    legacy: false,
  };
}

export type BroilerOnFarmCounts = {
  activeBatches: BroilerBatch[];
  activeBatchCount: number;
  startedBirds: number;
  onFarmBirds: number;
  mortality: number;
  // True when any active batch still counts its ordered birds.
  estimated: boolean;
};

export function computeBroilerOnFarmCounts(
  batches: readonly BroilerBatch[] | null | undefined,
  broilerDailys: readonly BroilerDailyRow[] | null | undefined,
  asOfDate: string | null | undefined = todayISO(),
): BroilerOnFarmCounts {
  const activeBatches = (batches || []).filter((b) => calcPoultryStatus(b, asOfDate) === 'active');
  return activeBatches.reduce<BroilerOnFarmCounts>(
    (acc, batch) => {
      const stats = calcBroilerStatsFromDailys(batch, broilerDailys);
      const started = stats.startedBirds;
      acc.startedBirds += started;
      acc.onFarmBirds += stats.projectedBirds;
      acc.mortality += stats.mortality;
      if (stats.startedEstimated) acc.estimated = true;
      return acc;
    },
    {
      activeBatches: [...activeBatches],
      activeBatchCount: activeBatches.length,
      startedBirds: 0,
      onFarmBirds: 0,
      mortality: 0,
      estimated: false,
    },
  );
}

// ============================================================================
// Timeline + batch-color deps (added in Round 6 timeline-view extraction)
// ----------------------------------------------------------------------------
// These started life as plain module-scope names in main.jsx. Moved here so
// the extracted timeline/list/feed views can import them without a circular
// dep on the entry module. breedLabel reads LEGACY_BREEDS (also lifted).
// ============================================================================
export const WEEKS_SHOWN = 52;

export const LEGACY_BREEDS = [
  {code: 'FR', label: 'Freedom Rangers'},
  {code: 'CY', label: 'Color Yields'},
];

export const RESOURCES = [
  {type: 'brooder', id: '1', label: 'Brooder 1'},
  {type: 'brooder', id: '2', label: 'Brooder 2'},
  {type: 'brooder', id: '3', label: 'Brooder 3'},
  {type: 'schooner', id: '1', label: 'Schooner 1'},
  {type: 'schooner', id: '2&3', label: 'Schooner 2 & 3'},
  {type: 'schooner', id: '4&5', label: 'Schooner 4 & 5'},
  {type: 'schooner', id: '6&6A', label: 'Schooner 6 & 6A'},
  {type: 'schooner', id: '7&7A', label: 'Schooner 7 & 7A'},
];

// ── Per-batch color system (Design Constitution Table H / Q08, D02) ────────
// One six-color palette for groups AND chart series — the same teal / plum /
// rose / bronze / slate / indigo the group palette uses, in the approved
// fixed order. The former 24-color rainbow is gone: it included the status
// hues (red / green / amber), which D02 reserves for state and never for a
// series. Brooder and schooner phases of one batch share the literally
// identical color. Used in timeline, list view, and feed calculator.
// Assignment stays the sequential batch-number index (B-26-09 → 9), so a
// batch keeps its color for life and the six wrap in the approved order.
const BATCH_COLOR_PALETTE = [
  {bg: '#1F7A8C', tx: 'white', bd: '#1F7A8C'}, // 0 teal
  {bg: '#7A4FA0', tx: 'white', bd: '#7A4FA0'}, // 1 plum
  {bg: '#B04A7A', tx: 'white', bd: '#B04A7A'}, // 2 rose
  {bg: '#8C6A2E', tx: 'white', bd: '#8C6A2E'}, // 3 bronze
  {bg: '#4E6175', tx: 'white', bd: '#4E6175'}, // 4 slate
  {bg: '#4A55A2', tx: 'white', bd: '#4A55A2'}, // 5 indigo
];
// The batch's slot in the six-colour group palette. BATCH_COLOR_PALETTE above
// and the `--group-1..6` tokens (Table H / R06) are the same six hexes in the
// same order, so this one index drives BOTH: getBatchColor reads the palette
// array, while a caller using the shared group primitives passes it straight
// to groupColor(). That is what keeps a batch the same colour on the list, the
// dashboard and the Add Report pills.
export function getBatchColorIndex(name: string | null | undefined): number {
  if (!name) return 0;
  // Try to extract a trailing number from the name (e.g. "B-26-09" → 9, "L-26-01" → 1).
  // This guarantees consecutive batches get adjacent (and visually distinct) palette colors.
  const m = String(name).match(/(\d+)\s*$/);
  if (m) {
    const n = parseInt(m[1]);
    if (!isNaN(n)) return n % BATCH_COLOR_PALETTE.length;
  }
  // Fallback for batches without a trailing number: hash
  let hash = 5381;
  const s = String(name).toLowerCase().trim();
  for (let i = 0; i < s.length; i++) hash = ((hash << 5) + hash + s.charCodeAt(i)) | 0;
  return Math.abs(hash) % BATCH_COLOR_PALETTE.length;
}

export function getBatchColor(name: string | null | undefined): BroilerBatchColor {
  return BATCH_COLOR_PALETTE[getBatchColorIndex(name)];
}

// Table H: archived groups are gray. A processed batch KEEPS its name-derived
// slot (the color follows the entity for its life) but DISPLAYS the approved
// gray wherever the surface knows the state — a presentation flag over the
// same row, no data change. The gray is the --group-archived token (Q42: the
// --label gray, 5.02:1 on white, since #9AA1A9 failed the 3:1 object rule); a
// filled bar carries white ink on it (also 5.02:1).
export const BATCH_ARCHIVED_COLOR = 'var(--group-archived)';

export function getBatchDisplayColor(batch: {name?: string | null; status?: string | null}): BroilerBatchColor {
  if (batch.status === 'processed') return {bg: BATCH_ARCHIVED_COLOR, tx: '#fff', bd: BATCH_ARCHIVED_COLOR};
  return getBatchColor(batch.name);
}

export function breedLabel(code: string | null | undefined): string {
  if (code === 'CC') return 'Cornish Cross';
  if (code === 'WR') return 'White Ranger';
  const lb = LEGACY_BREEDS.find((x) => x.code === code);
  if (lb) return lb.label;
  return code || '\u2014';
}

// ============================================================================
// Status badge colors + US-holiday-adjacent date warnings (list view deps)
// ----------------------------------------------------------------------------
// STATUS_STYLE is used by the broiler list view for the planned/active/
// processed pills. isNearHoliday flags hatch/processing dates that fall
// within 1 day of a US holiday (processors close, so scheduling gets sticky).
// ============================================================================
export const STATUS_STYLE = {
  planned: {bg: '#374151', tx: 'white'},
  active: {bg: '#187A54', tx: 'white'},
  processed: {bg: '#4b5563', tx: 'white'},
};

function getEaster(y: number): string {
  const a = y % 19,
    b = Math.floor(y / 100),
    c = y % 100,
    d = Math.floor(b / 4),
    e = b % 4;
  const f = Math.floor((b + 8) / 25),
    g = Math.floor((b - f + 1) / 3),
    h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4),
    k = c % 4,
    l = (32 + 2 * e + 2 * i - h - k) % 7,
    m = Math.floor((a + 11 * h + 22 * l) / 451);
  return toISO(new Date(y, Math.floor((h + l - 7 * m + 114) / 31) - 1, ((h + l - 7 * m + 114) % 31) + 1));
}
function getThanksgiving(y: number): string {
  const d = new Date(y, 10, 1);
  return toISO(new Date(y, 10, ((4 - d.getDay() + 7) % 7) + 22));
}
function holidaysForYear(y: number): string[] {
  return [`${y}-01-01`, getEaster(y), `${y}-07-04`, getThanksgiving(y), `${y}-12-25`];
}
export function isNearHoliday(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso + 'T12:00:00'),
    y = d.getFullYear();
  const all = [...holidaysForYear(y - 1), ...holidaysForYear(y), ...holidaysForYear(y + 1)];
  // Date subtraction is getTime() subtraction (Date's valueOf); spelling it out
  // is the same arithmetic the checker can follow.
  return all.some((h) => Math.abs(d.getTime() - new Date(h + 'T12:00:00').getTime()) / 86400000 <= 1);
}

// ============================================================================
// Monthly feed projections (feed-view deps, lifted in Round 6)
// ----------------------------------------------------------------------------
// calcBatchFeedForMonth projects broiler feed for a calendar month using
// the same schedule as calcBatchFeed. calcLayerFeedForMonth does the same
// for layer batches — starter/grower phases drive off original_count, layer
// phase uses computeProjectedCount against the active housings.
// ============================================================================
// For monthly summary: given a batch and a calendar month (YYYY-MM),
// return {starter, grower} lbs consumed in that month
export function calcBatchFeedForMonth(batch: BroilerBatch, yearMonth: string): {starter: number; grower: number} {
  if (!batch.hatchDate) return {starter: 0, grower: 0};
  // Bound to a const so the guard above survives into the callback below — a
  // property narrowing does not, because the object could in principle be
  // reassigned between the check and the call.
  const hatchDate = batch.hatchDate;
  const schedule = getFeedSchedule(batch.breed, batch.growoutWeeks);
  const [y, m] = yearMonth.split('-').map(Number);
  const monthStart = new Date(y, m - 1, 1);
  const monthEnd = new Date(y, m, 0, 23, 59, 59);
  let starter = 0,
    grower = 0;
  schedule.forEach((w, i) => {
    const weekStart = addDays(hatchDate, i * 7);
    const weekEnd = addDays(hatchDate, (i + 1) * 7 - 1);
    // Check overlap with month
    if (weekStart <= monthEnd && weekEnd >= monthStart) {
      // Proportion of week that falls in this month
      const overlapStart = weekStart < monthStart ? monthStart : weekStart;
      const overlapEnd = weekEnd > monthEnd ? monthEnd : weekEnd;
      const overlapDays = (overlapEnd.getTime() - overlapStart.getTime()) / 86400000 + 1;
      const prop = overlapDays / 7;
      if (w.phase === 'starter') starter += w.totalLbs * prop;
      else grower += w.totalLbs * prop;
    }
  });
  return {starter: Math.round(starter), grower: Math.round(grower)};
}

// ── LAYER FEED SCHEDULE ──────────────────────────────────────────────────
// Weeks 1-6: starter, Weeks 7-20: grower, Week 21+: layer feed (0.25 lbs/bird/day)
// Starter capped at 1,500 lbs per batch (same as broilers).
export const LAYER_FEED_SCHEDULE = [
  {week: 1, phase: 'starter', lbsPerBird: 0.5},
  {week: 2, phase: 'starter', lbsPerBird: 1.0},
  {week: 3, phase: 'starter', lbsPerBird: 1.1},
  {week: 4, phase: 'starter', lbsPerBird: 1.2},
  {week: 5, phase: 'starter', lbsPerBird: 1.2},
  {week: 6, phase: 'starter', lbsPerBird: 1.0},
  {week: 7, phase: 'grower', lbsPerBird: 0.6},
  {week: 8, phase: 'grower', lbsPerBird: 0.65},
  {week: 9, phase: 'grower', lbsPerBird: 0.7},
  {week: 10, phase: 'grower', lbsPerBird: 0.75},
  {week: 11, phase: 'grower', lbsPerBird: 0.8},
  {week: 12, phase: 'grower', lbsPerBird: 0.8},
  {week: 13, phase: 'grower', lbsPerBird: 0.85},
  {week: 14, phase: 'grower', lbsPerBird: 0.85},
  {week: 15, phase: 'grower', lbsPerBird: 0.9},
  {week: 16, phase: 'grower', lbsPerBird: 0.9},
  {week: 17, phase: 'grower', lbsPerBird: 0.95},
  {week: 18, phase: 'grower', lbsPerBird: 0.95},
  {week: 19, phase: 'grower', lbsPerBird: 1.0},
  {week: 20, phase: 'grower', lbsPerBird: 1.0},
];
export const LAYER_FEED_PER_DAY = 0.25; // lbs/bird/day on layer feed (week 21+)

// For a layer batch + month, return {starter, grower, layer} lbs projected.
// Uses brooder_entry_date as day 0, original_count for starter/grower phases,
// computeProjectedCount for layer phase hen count.
export function calcLayerFeedForMonth(
  batch: LayerBatchRow,
  housings: readonly LayerHousingRow[] | null | undefined,
  layerDailys: readonly LayerDailyRow[] | null | undefined,
  yearMonth: string,
): {starter: number; grower: number; layer: number} {
  const startDate = batch.brooder_entry_date || batch.arrival_date;
  if (!startDate) return {starter: 0, grower: 0, layer: 0};
  const birdCount = parseInt(String(batch.original_count)) || 0;
  if (birdCount <= 0) return {starter: 0, grower: 0, layer: 0};
  const [y, m] = yearMonth.split('-').map(Number);
  const monthStart = new Date(y, m - 1, 1);
  const monthEnd = new Date(y, m, 0, 23, 59, 59);
  let starter = 0,
    grower = 0,
    layer = 0;
  // Weeks 1-20: starter/grower from schedule
  LAYER_FEED_SCHEDULE.forEach(function (w, i) {
    const weekStart = addDays(startDate, i * 7);
    const weekEnd = addDays(startDate, (i + 1) * 7 - 1);
    if (weekStart <= monthEnd && weekEnd >= monthStart) {
      const overlapStart = weekStart < monthStart ? monthStart : weekStart;
      const overlapEnd = weekEnd > monthEnd ? monthEnd : weekEnd;
      const overlapDays = (overlapEnd.getTime() - overlapStart.getTime()) / 86400000 + 1;
      const prop = overlapDays / 7;
      const lbs = w.lbsPerBird * birdCount * prop;
      if (w.phase === 'starter') starter += lbs;
      else grower += lbs;
    }
  });
  // Week 21+: layer feed at 0.25 lbs/bird/day using projected hen count
  var layerStart = addDays(startDate, 20 * 7); // day 140
  if (layerStart <= monthEnd) {
    var lStart = layerStart < monthStart ? monthStart : layerStart;
    var lEnd = monthEnd;
    var lDays = Math.max(0, (lEnd.getTime() - lStart.getTime()) / 86400000 + 1);
    if (lDays > 0) {
      // Use projected hen count from active housings for this batch
      var batchHousings = (housings || []).filter(function (h) {
        return h.batch_id === batch.id && h.status === 'active';
      });
      var hens = 0;
      if (batchHousings.length > 0) {
        batchHousings.forEach(function (h) {
          var proj = computeProjectedCount(h, layerDailys, housings);
          // computeProjectedCount's `projected` is declared nullable (it mirrors
          // its own nullable anchor); `?? 0` is the same contribution `hens +=
          // null` already made, stated so the sum stays a number.
          hens += proj ? (proj.projected ?? 0) : parseInt(String(h.current_count)) || 0;
        });
      } else {
        hens = birdCount; // fallback if no housings yet
      }
      layer += hens * LAYER_FEED_PER_DAY * lDays;
    }
  }
  return {starter: Math.round(starter), grower: Math.round(grower), layer: Math.round(layer)};
}

// Recompute the broiler session's average and write it to the matching
// batch's week4Lbs / week6Lbs in app_store.ppp-v4. ONLY runs for sessions
// already marked complete -- draft saves never bleed into the batch tile.
//
// Called from admin-only call sites (LivestockWeighInsView's
// completeFromAdmin, saveAdminGrid, and the metadata-edit week-change
// recompute). The public WeighInsWebform finalizeSession routes through
// the SECURITY DEFINER stamp_broiler_batch_avg RPC instead (mig 055),
// because anon RLS blocks direct app_store reads/writes.
//
// broiler_week is coerced via Number() so callers that pass a string
// (e.g. test fixtures, JSON-roundtripped sessions) still gate correctly.
export type BroilerSessionRow = {
  species?: string | null;
  status?: string | null;
  broiler_week?: number | string | null;
  batch_id?: string | null;
  [column: string]: unknown;
};

export async function writeBroilerBatchAvg(
  sb: SupabaseClient | null | undefined,
  sessionRow: BroilerSessionRow | null | undefined,
  sessionEntries: readonly WeighInWeightRow[] | null | undefined,
): Promise<BroilerWriteResult> {
  if (!sb || !sessionRow || sessionRow.species !== 'broiler') return {ok: true};
  if (sessionRow.status !== 'complete') return {ok: true};
  var weekNum = Number(sessionRow.broiler_week);
  if (!sessionRow.batch_id || (weekNum !== 4 && weekNum !== 6)) return {ok: true};
  if (!sessionEntries || sessionEntries.length === 0) return {ok: true};
  var avg = calcBroilerSessionAverageLbs(sessionEntries);
  if (avg == null) return {ok: true};
  // 236: ONE audited server operation under the ppp-v4 row lock, instead of
  // reading the whole batch list and upserting it back. The session stores the
  // batch NAME; batch.week_avg resolves it (id first, then exact name), and an
  // unknown batch is the same benign no-op it has always been.
  return await stampBroilerWeekAverage(sb, sessionRow.batch_id, weekNum, avg);
}

export function calcBroilerSessionAverageLbs(
  sessionEntries: readonly WeighInWeightRow[] | null | undefined,
): number | null {
  if (!Array.isArray(sessionEntries) || sessionEntries.length === 0) return null;
  var sum = 0,
    n = 0;
  for (var i = 0; i < sessionEntries.length; i++) {
    var w = parseFloat(String(sessionEntries[i].weight));
    if (!isNaN(w) && w > 0) {
      sum += w;
      n++;
    }
  }
  if (n === 0) return null;
  return Math.round((sum / n) * 100) / 100;
}

export type BroilerWeekAverage = {
  ok: boolean;
  message?: string;
  averageLbs?: number | null;
  sessionId?: string | null;
};

async function loadLatestBroilerWeekAverage(
  sb: SupabaseClient | null | undefined,
  batchId: string | null | undefined,
  week: number,
  opts?: {excludeSessionId?: string | null} | null,
): Promise<BroilerWeekAverage> {
  if (!sb || !batchId || (week !== 4 && week !== 6)) return {ok: true, averageLbs: null};
  const excludeSessionId = opts && opts.excludeSessionId ? opts.excludeSessionId : null;
  let q = sb
    .from('weigh_in_sessions')
    .select('id, completed_at')
    .eq('species', 'broiler')
    .eq('batch_id', batchId)
    .eq('broiler_week', week)
    .eq('status', 'complete');
  if (excludeSessionId) q = q.neq('id', excludeSessionId);
  const sR = await q.order('completed_at', {ascending: false}).limit(1);
  if (sR && sR.error) return {ok: false, message: 'sessions read failed: ' + sR.error.message};
  const list: BroilerWeekSessionRow[] = (sR && sR.data) || [];
  if (list.length === 0) return {ok: true, averageLbs: null, sessionId: null};

  const latestId = list[0].id;
  const eR = await sb.from('weigh_ins').select('weight').eq('session_id', latestId);
  if (eR && eR.error) return {ok: false, message: 'entries read failed: ' + eR.error.message};
  const entries: WeighInWeightRow[] = (eR && eR.data) || [];
  return {
    ok: true,
    averageLbs: calcBroilerSessionAverageLbs(entries),
    sessionId: latestId,
  };
}

export async function loadBroilerBatchWeekAverages(
  sb: SupabaseClient | null | undefined,
  batchId: string | null | undefined,
): Promise<{ok: boolean; message?: string; week4Lbs: number | null; week6Lbs: number | null}> {
  if (!sb || !batchId) return {ok: true, week4Lbs: null, week6Lbs: null};
  const week4 = await loadLatestBroilerWeekAverage(sb, batchId, 4);
  if (!week4.ok) return {ok: false, message: week4.message, week4Lbs: null, week6Lbs: null};
  const week6 = await loadLatestBroilerWeekAverage(sb, batchId, 6);
  if (!week6.ok) return {ok: false, message: week6.message, week4Lbs: week4.averageLbs ?? null, week6Lbs: null};
  return {ok: true, week4Lbs: week4.averageLbs ?? null, week6Lbs: week6.averageLbs ?? null};
}

// Recompute the wk*Lbs field on app_store.ppp-v4[batch] for a given
// (batchId, week) pair, optionally excluding one session id from
// consideration. Used by the admin metadata-edit flow when a completed
// broiler session's broiler_week changes — the OLD week's stored avg
// must be re-derived from the latest OTHER complete session, or cleared
// if no other session backs that week.
//
// Result contract:
//   {ok: true}                — successful recompute, successful delete,
//                               OR intentional no-op (no usable entries
//                               on the picked session, batch row not
//                               found in ppp-v4, ppp-v4 row missing).
//   {ok: false, message: string}
//                              — Supabase read or upsert returned an
//                               error. Caller surfaces this to the
//                               admin via metaErr.
//
// Last-write-wins semantics preserved: when multiple complete sessions
// exist for (batchId, week), the latest by completed_at wins (matches
// today's writeBroilerBatchAvg behaviour where the most recent caller
// stamps its avg). No cross-session aggregation here.
export async function recomputeBroilerBatchWeekAvg(
  sb: SupabaseClient | null | undefined,
  batchId: string | null | undefined,
  week: number,
  opts?: {excludeSessionId?: string | null} | null,
): Promise<BroilerWriteResult> {
  if (!sb || !batchId || (week !== 4 && week !== 6)) return {ok: true};
  const excludeSessionId = opts && opts.excludeSessionId ? opts.excludeSessionId : null;
  const latest = await loadLatestBroilerWeekAverage(sb, batchId, week, {excludeSessionId});
  if (!latest.ok) return {ok: false, message: latest.message};

  if (!latest.sessionId) {
    // No backing session — drop the wk*Lbs key from the batch entirely.
    return await stampBroilerWeekAverage(sb, batchId, week, null);
  }

  if (latest.averageLbs == null) return {ok: true}; // intentional no-op — no usable entries
  return await stampBroilerWeekAverage(sb, batchId, week, latest.averageLbs);
}

// Set (or, with null, remove) a batch's week-4/6 average through ONE audited
// server operation (migration 236, batch.week_avg). It replaces the
// read-the-whole-list-and-upsert-it-back writer that used to live here, which
// matched the batch by name and could overwrite a concurrent edit of any
// other batch. `batchRef` is what a weigh-in session stores: the batch name
// (an id also resolves). Same result contract as before: an absent list or
// batch is a successful no-op; only a refusal or a lost answer is {ok: false}.
async function stampBroilerWeekAverage(
  sb: SupabaseClient,
  batchRef: string,
  week: number,
  lbs: number | null,
): Promise<BroilerWriteResult> {
  const {data, error} = await applyBroilerOperation(sb, {
    operation: 'batch.week_avg',
    target: {batch_ref: batchRef},
    expected: {},
    change: {week, lbs},
  });
  if (error || !data) {
    const message = error && (error as {message?: unknown}).message;
    return {ok: false, message: 'ppp-v4 week average failed: ' + (typeof message === 'string' ? message : 'no answer')};
  }
  return {ok: true};
}
